/**
 * Secure Authentication Token Helper
 * 
 * This utility provides a secure way to get Firebase ID tokens
 * instead of using JSON.stringify({ id, role }) which can be forged.
 */

import { getCurrentUser, getIdToken } from '../firebase/auth';

// Token cache to prevent duplicate refresh calls
let tokenCache = {
  token: null,
  expiresAt: 0,
  promise: null
};

// Cache duration: 1 minute (tokens are valid for 1 hour, but we cache for 1 min to reduce refresh calls)
const CACHE_DURATION_MS = 60 * 1000;

/**
 * Get a secure Firebase ID token for API authentication
 * @param {boolean} forceRefresh - Force token refresh
 * @returns {Promise<string|null>} Firebase ID token or null if not authenticated
 */
export const getAuthToken = async (forceRefresh = false) => {
  try {
    const user = getCurrentUser();
    if (!user) {
      // Clear cache if no user
      tokenCache = { token: null, expiresAt: 0, promise: null };
      // Only log warning in development or when explicitly needed
      // Suppress warning for normal unauthenticated requests
      if (process.env.NODE_ENV === 'development') {
        console.warn('No authenticated user found');
      }
      return null;
    }
    
    const now = Date.now();
    
    // If we have a cached token that's still valid and not forcing refresh, return it
    if (!forceRefresh && tokenCache.token && tokenCache.expiresAt > now && tokenCache.promise) {
      // If there's an ongoing promise, wait for it instead of making a new request
      return await tokenCache.promise;
    }
    
    // If there's already an ongoing token fetch, reuse that promise
    if (tokenCache.promise && !forceRefresh) {
      return await tokenCache.promise;
    }
    
    // Create a new promise for fetching the token
    const tokenPromise = getIdToken(forceRefresh).then(token => {
      if (token) {
        // Cache the token with expiration
        tokenCache.token = token;
        tokenCache.expiresAt = now + CACHE_DURATION_MS;
      } else {
        // Clear cache if token is null
        tokenCache = { token: null, expiresAt: 0, promise: null };
      }
      // Clear the promise after it resolves
      tokenCache.promise = null;
      return token;
    }).catch(error => {
      // Clear cache on error
      tokenCache = { token: null, expiresAt: 0, promise: null };
      console.error('Error getting auth token:', error);
      return null;
    });
    
    // Store the promise so concurrent calls can reuse it
    tokenCache.promise = tokenPromise;
    
    return await tokenPromise;
  } catch (error) {
    // Clear cache on error
    tokenCache = { token: null, expiresAt: 0, promise: null };
    console.error('Error getting auth token:', error);
    return null;
  }
};

/**
 * Get auth headers for fetch requests
 * @param {boolean} forceRefresh - Force token refresh
 * @returns {Promise<object>} Headers object with Authorization header
 */
export const getAuthHeaders = async (forceRefresh = false) => {
  const token = await getAuthToken(forceRefresh);
  
  if (!token) {
    return {};
  }
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use getAuthToken() instead
 */
export const getLegacyAuthToken = (user) => {
  console.warn('getLegacyAuthToken is deprecated. Use getAuthToken() instead.');
  if (!user) return null;
  return JSON.stringify({ id: user.uid || user.id, role: user.role });
};


